import { AudioFrameV2, AudioInfoV2 } from '@mimir/cutter/frameProvider/frameProvider';
import { calculateRawAudioFrameBufferSize } from '@mimir/common/transcoding/rawFrame';
import { RefCountedBuffer, BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';
import { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { binarySearch } from '@mimir/common/util/binarySearch';

export interface AudioCache {
  sampleRate(): number | null;
  push(audioFrame: AudioFrameV2): void;
  get(sampleOffset: number, sampleCount: number): AudioFrameV2 | null;
  has(sampleOffset: number, sampleCount: number): boolean;
  remove(startSampleOffset: number, endSampleOffset: number): void;
  ranges(): { start: number, end: number }[];
  clear(): void;
  close(): void;
}

export function formatRanges(ranges: { start: number, end: number }[]): string {
  return `(${ranges.map((r) => `[${r.start}, ${r.end}>`).join(', ')})`;
}

export function createAudioCache(logger: StructuredLogger, bufferCache: BufferCache) {
  return createAudioCacheInternal(logger, bufferCache);
}

// ----

interface CacheEntry {
  sampleOffset: number,
  sampleCount: number,
  bufferOffset: number,
  buffer: RefCountedBuffer,
}

function createAudioCacheInternal(logger: StructuredLogger, bufferCache: BufferCache) {
  let info : AudioInfoV2 | null = null;

  // We maintain two different data structures:
  //
  // The cache contains the actual audio frames and each frame will represent
  // its own sample range in the cache so that the number of frames == number
  // of ranges. This structure provides fast insert/deletion of audio frames,
  // but slow lookup for availability.
  const cache : CacheEntry[] = [];

  // The ranges contains only information about the available samples in the
  // cache, but also merges contiguous ranges so that if all audio frames in
  // the cache are contiguous then this structure will only hold a single range
  // for fast availability lookup.
  let ranges = new SampleRanges();

  return {
    sampleRate(): number | null {
      return info?.sampleRate ?? null
    },
    push(audioFrame: AudioFrameV2): void {
      logger.log({ level: 'debug', message: 'AudioCache.push() {' });
      if (info === null) {
        info = {
          sampleRate: audioFrame.sampleRate,
          channelCount: audioFrame.channelCount,
          format: audioFrame.format,
        };
      } else {
        if (info.sampleRate !== audioFrame.sampleRate) {
          throw new Error('Inconsistent sample rate pushed into AudioCache');
        }
        if (info.channelCount !== audioFrame.channelCount) {
          throw new Error('Inconsistent channel count pushed into AudioCache');
        }
        if (info.format !== audioFrame.format) {
          throw new Error('Inconsistent audio format pushed into AudioCache');
        }
      }
      ranges.add(audioFrame.sampleOffset, audioFrame.sampleOffset + audioFrame.sampleCount);
      const expectedBufferSize = calculateRawAudioFrameBufferSize(audioFrame, audioFrame.sampleCount);
      if (expectedBufferSize !== audioFrame.buffer.size()) {
        throw new Error(`Bad buffer size: ${expectedBufferSize} != ${audioFrame.buffer.size()}`);
      }
      deleteAndTrimCache(logger, audioFrame.sampleOffset, audioFrame.sampleCount, cache, info);
      cache.push({
        sampleOffset: audioFrame.sampleOffset,
        sampleCount: audioFrame.sampleCount,
        bufferOffset: 0,
        buffer: audioFrame.buffer,
      });
      cache.sort((a, b) => a.sampleOffset - b.sampleOffset);

      logger.log({ level: 'debug', message: '}' });
    },
    has(sampleOffset: number, sampleCount: number): boolean {
      return ranges.has(sampleOffset, sampleOffset + sampleCount);
    },
    get(sampleOffset: number, sampleCount: number): AudioFrameV2 | null {
      logger.log({ level: 'debug', message: 'AudioCache.get() {', sampleOffset, sampleCount });

      if (!ranges.has(sampleOffset, sampleOffset + sampleCount)) {
        return null;
      }

      if (info === null) {
        return null;
      }
      const audioFrame = getRange(logger, sampleOffset, sampleCount, info, cache, bufferCache);
      logger.log({ level: 'debug', message: '}' });
      return audioFrame;
    },
    ranges(): { start: number, end: number }[] {
      return ranges.ranges;
    },
    remove(startSampleOffset: number, endSampleOffset: number): void {
      logger.log({ level: 'debug', message: 'AudioCache.remove() {', startSampleOffset, endSampleOffset });
      if (info === null) {
        return;
      }
      deleteAndTrimCache(logger, startSampleOffset, endSampleOffset - startSampleOffset, cache, info);
      ranges.remove(startSampleOffset, endSampleOffset);
    },
    clear(): void {
      for (const entry of cache.splice(0)) {
        entry.buffer.close();
      }
      ranges.clear();
    },
    close(): void {
      this.clear();
    }
  };
}

function trimEntryStart(
  entry: CacheEntry,
  info: AudioInfoV2,
  sampleCount: number,
): CacheEntry {
  if (sampleCount < 0) {
    throw new Error('Trimming negative sample count');
  }
  if (sampleCount >= entry.sampleCount) {
    throw new Error('Trimming too much from start of audio frame');
  }

  const offset = calculateRawAudioFrameBufferSize(info, sampleCount);

  return {
    sampleCount: (entry.sampleCount - sampleCount),
    sampleOffset: (entry.sampleOffset + sampleCount),
    bufferOffset: entry.bufferOffset + offset,
    buffer: entry.buffer.addReference(),
  };
}

function trimEntryEnd(entry: CacheEntry, sampleCount: number): CacheEntry {
  if (sampleCount < 0) {
    throw new Error('Trimming negative sample count');
  }
  if (sampleCount >= entry.sampleCount) {
    throw new Error('Trimming too much from start of audio frame');
  }

  return {
    sampleCount: entry.sampleCount - sampleCount,
    sampleOffset: entry.sampleOffset,
    bufferOffset: entry.bufferOffset,
    buffer: entry.buffer.addReference(),
  };
}

function deleteAndTrimCache(
  logger: StructuredLogger,
  sampleOffset: number,
  sampleCount: number,
  cache: CacheEntry[],
  info: AudioInfoV2,
) {
  logger.log({ level: 'debug', message: '  deleteAndTrimCache()', sampleOffset, sampleCount });
  const start = sampleOffset;
  const end = sampleOffset + sampleCount;
  let frameIndex = 0;

  // When we split a entry in two we create new frames that we will
  // push onto the cache after the main lop
  const newEntries : CacheEntry[] = [];
  while (true) {
    const entry = cache[frameIndex];
    if (entry === undefined) {
      break;
    }
    const frameStart = entry.sampleOffset;
    const frameEnd = entry.sampleOffset + entry.sampleCount;

    logger.log({ level: 'debug', message: '    deleteAndTrimCache:', start, end,  frameStart, frameEnd });

    if ((start <= frameStart) && (end >= frameEnd)) {
      // range:     |------------|
      // entry:          |---|
      // delete:         |---|
      // keep:
      logger.log({ level: 'debug', message: '    whole entry removed' });
      cache.splice(frameIndex, 1);
      entry.buffer.close();
      continue;
    } else if ((start > frameStart) && (start < frameEnd)) {
      // range:         |---..?
      // entry:       |-------|
      // delete:        |-----|
      // keep:        |-|
      const toRemove = frameEnd - start;
      logger.log({ level: 'debug', message: '    remove samples from end of entry', toRemove });
      cache[frameIndex] = trimEntryEnd(entry, toRemove);
      if ((end >= frameStart) && (end < frameEnd)) {
        // range:         |---|
        // entry:       |-------|
        // delete:      |-----|
        // keep:              |-|
        const toRemove = end - frameStart;
        logger.log({ level: 'debug', message: '    duplicate and remove samples from start of entry', toRemove });
        newEntries.push(trimEntryStart(entry, info, toRemove));
      }
      entry.buffer.close();
    } else if ((end > frameStart) && (end < frameEnd)) {
      // range:       |---|
      // entry:       |-------|
      // delete:      |---|
      // keep:            |---|
      const toRemove = end - frameStart;
      logger.log({ level: 'debug', message: '    remove samples from start of entry', toRemove });
      cache[frameIndex] =  trimEntryStart(entry, info, toRemove);
      entry.buffer.close();
    }
    frameIndex += 1;
  }

  if (newEntries.length > 0) {
    cache.push(...newEntries);
    cache.sort((a, b) => a.sampleOffset - b.sampleOffset);
  }
  logger.log({ level: 'debug', message: '  }' });
  return cache;
}

function getRange(
  logger: StructuredLogger,
  sampleOffset: number,
  sampleCount: number,
  info: AudioInfoV2,
  cache: CacheEntry[],
  bufferCache: BufferCache,
): AudioFrameV2 | null {
  logger.log({ level: 'debug', message: '  getRange()', sampleOffset, sampleCount });
  if (cache.length === 0) {
    logger.log({ level: 'debug', message: '    empty cache' });
    return null;
  }

  let start = sampleOffset;
  const end = sampleOffset + sampleCount;
  const entries : CacheEntry[] = [];
  let currentEnd = sampleOffset;

  let index =
    binarySearch<{ sampleOffset: number }>(
      cache,
      { sampleOffset },
      (a, b) => a.sampleOffset - b.sampleOffset
    );

  // If there are no entries greater or equal to the requested sample offset
  // it could still be that the last element covers the requested range.
  if (index === -1) {
    index = cache.length - 1;
  }

  // Binary search return greater or equal. We want less or equal
  if (cache[index].sampleOffset > sampleOffset) {
    index --;
  }

  if (index < 0) {
    logger.log({ level: 'debug', message: '    All entries are greater', sampleOffset });
    return null;
  }

  if (cache[index].sampleOffset + cache[index].sampleCount <= sampleOffset) {
    logger.log({ level: 'debug', message: '    The highest frame with sample offset less or equal end before the requested offset', sampleOffset });
    return null;
  }

  while (index < cache.length) {
    const frame = cache[index];
    const frameStart = frame.sampleOffset;
    const frameEnd = frame.sampleOffset + frame.sampleCount;

    logger.log({ level: 'debug', message: '    getRange:', start, end, frameStart, frameEnd });

    if (end <= frameStart) {
      logger.log({ level: 'debug', message: '    End before start of frame. None of the upcoming entries are relevant' });
      // we're done
      break;
    }

    if (start < frameStart) {
      logger.log({ level: 'debug', message: '    Start range before frame, samples not available' });
      // Requested range starts before the current frame rate
      logger.log({ level: 'debug', message: '  }' });
      entries.forEach(e => e.buffer.close());
      return null;
    }

    if (start >= frameEnd) {
      throw new Error('Binary search failed');
    }

    let trimmedFrame : CacheEntry = {
      ...frame,
      buffer: frame.buffer.addReference()
    };

    if (start > frameStart) {
      logger.log({ level: 'debug', message: '    trimEntryStart', count: start - frameStart });
      const tmp = trimEntryStart(trimmedFrame, info, start - frameStart);
      trimmedFrame.buffer.close();
      trimmedFrame = tmp;
    }
    if (end < frameEnd) {
      logger.log({ level: 'debug', message: '    trimEntryEnd', count: frameEnd - end });
      const tmp = trimEntryEnd(trimmedFrame, frameEnd - end);
      trimmedFrame.buffer.close();
      trimmedFrame = tmp;
    }

    entries.push(trimmedFrame);
    currentEnd = trimmedFrame.sampleOffset + trimmedFrame.sampleCount;
    start = trimmedFrame.sampleOffset + trimmedFrame.sampleCount;
    index ++;
  }

  logger.log({ level: 'debug', message: '  }' });

  if ((entries.length === 0) || (currentEnd < end)) {
    entries.forEach(e => e.buffer.close());
    return null;
  }
  if (currentEnd !== end) {
    throw new Error(`Something went wrong. currentEnd=${currentEnd}, end=${end}`);
  }


  const audioBufferSize = calculateRawAudioFrameBufferSize(info, sampleCount);

  return {
    sampleOffset,
    sampleCount,
    sampleRate: info.sampleRate,
    channelCount: info.channelCount,
    format: info.format,
    buffer: bufferCache.allocateBuffer(
      audioBufferSize,
      (buffer: Uint8Array) => {
        let dstOffset : number = 0;
        for (const entry of entries) {
          const src = entry.buffer.asReadonlyUint8Array();
          let srcOffset = entry.bufferOffset;
          let srcBufferSize = calculateRawAudioFrameBufferSize(info, entry.sampleCount);
          entry.buffer.close();
          for (let i = 0; i < srcBufferSize; i++) {
            buffer[dstOffset] = src[srcOffset];
            srcOffset ++;
            dstOffset ++;
          }
        }
      }
    ),
  };
}

class SampleRanges {
  // Working buffer to avoid reallocation
  private deltas: { offset: number, delta: number }[] = [];

  public readonly ranges: { start: number, end: number }[] = [];

  constructor() { }

  clear() {
    this.ranges.splice(0);
  }

  add(start: number, end: number) {
    this.deltas.splice(0);
    for (const range of this.ranges) {
      this.deltas.push({ offset: range.start, delta: 1 });
      this.deltas.push({ offset: range.end, delta: -1 });
    }
    this.deltas.push({ offset: start, delta: 1 });
    this.deltas.push({ offset: end, delta: -1 });
    this.deltas.sort((a, b) => a.offset - b.offset);
    this.updateFromDeltas();
  }

  remove(start: number, end: number) {
    this.deltas.splice(0);
    for (const range of this.ranges) {
      this.deltas.push({ offset: range.start, delta: 1 });
      this.deltas.push({ offset: range.end, delta: -1 });
    }
    this.deltas.push({ offset: start, delta: -1 });
    this.deltas.push({ offset: end, delta: 1 });
    this.deltas.sort((a, b) => a.offset - b.offset);
    this.updateFromDeltas();
  }

  has(start: number, end: number): boolean {
    for (const range of this.ranges) {
      if (range.end <= start) {
        continue;
      }

      if (range.start > start) {
        return false;
      }

      return (range.end >= end);
    }

    return false;
  }

  updateFromDeltas() {
    let currentRangeStart : number | null = null;

    this.ranges.splice(0);
    let value = 0;
    let lastOffset = this.deltas[0].offset;
    for (const { offset, delta } of this.deltas) {
      if (offset !== lastOffset) {
        if (value > 0 && currentRangeStart === null) {
          currentRangeStart = lastOffset;
        }
        if (value <= 0 && currentRangeStart !== null) {
          this.ranges.push({ start: currentRangeStart, end: lastOffset });
          currentRangeStart = null;
        }
      }
      value += delta;
      lastOffset = offset;
    }
    if (value <= 0 && currentRangeStart !== null) {
      this.ranges.push({ start: currentRangeStart, end: lastOffset });
      currentRangeStart = null;
    }
  }
}
